import { Container, Header, MessageList } from '@botpress/webchat'
import { LatestWebchatTheme, apcaContrast } from '@bpinternal/shared'
import { Label, Popover, PopoverContent, PopoverTrigger, RadioGroup, Slider } from '@bpinternal/ui-kit-next'
import allFonts from 'google-fonts-complete/api-response.json'
import { useMemo, type FC } from 'react'
import { RadioCard } from '~/elements/RadioCard'
import { ConfigSection } from '~/features/webchat/components'
import { HexColorInput } from '~/features/webchat/components/elements/HexColorInput'
import { getUseConfigStore } from '~/features/webchat/stores/useConfigStore'
import { useLocalStore } from '~/stores'
import WebchatComponentPreview from '../elements/WebchatComponentPreview'
import { cn } from '~/utils'
import { HexColorPopover } from '../elements/HexColorPopover'
import { FontCombobox } from '../elements/FontCombobox'

export type FontMeta = {
  family: string
  category?: string
}

type Props = {
  workspaceId: string
  botId: string
  title?: string
  description?: string
}

export const AppearanceSection: FC<Props> = ({
  workspaceId,
  botId,
  title = 'Appearance',
  description = 'Customize the look and feel of your bot.',
}) => {
  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])

  const color = useConfigStore((state) => state.color)
  const themeMode = useConfigStore((state) => state.themeMode)
  const variant = useConfigStore((state) => state.variant)
  const headerVariant = useConfigStore((state) => state.headerVariant)
  const fontFamily = useConfigStore((state) => state.fontFamily)
  const radius = useConfigStore((state) => state.radius)
  const setThemeMode = useConfigStore((state) => state.setThemeMode)
  const setVariant = useConfigStore((state) => state.setVariant)
  const setColor = useConfigStore((state) => state.setColor)
  const setFont = useConfigStore((state) => state.setFontFamily)
  const setRadius = useConfigStore((state) => state.setRadius)
  const setHeaderVariant = useConfigStore((state) => state.setHeaderVariant)

  const dashboardTheme = useLocalStore((state) => state.getPreferredTheme())

  const fonts: FontMeta[] = Object.entries(allFonts).map(([, meta]) => ({
    family: meta.family,
    category: meta.category,
  }))

  const webchatTheme: LatestWebchatTheme = {
    themeMode: dashboardTheme,
    headerVariant,
    variant,
    color,
    radius,
  }

  return (
    <ConfigSection title={title} description={description}>
      <div className="flex flex-col gap-10">
        <div className="flex flex-col">
          <Popover>
            <PopoverTrigger asChild>
              <div
                className={cn(
                  'flex w-full cursor-pointer items-center justify-center rounded-t-lg py-6 font-medium',
                  apcaContrast(dashboardTheme === 'dark' ? '#ffffff' : '#000000', color) > 60
                    ? 'text-neutral-950'
                    : 'text-neutral-50'
                )}
                style={{ background: color }}
              >
                Primary Color
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-min p-3">
              <HexColorPopover color={color} setColor={setColor} />
            </PopoverContent>
          </Popover>
          <HexColorInput className="!rounded-t-none" value={color} onChange={setColor} />
        </div>
        <div className="flex w-full flex-col gap-2">
          <Label className="text-neutral-950">Theme Mode</Label>
          <RadioGroup
            className="flex w-full flex-col xl:flex-row"
            onValueChange={(value: 'light' | 'dark') => setThemeMode(value)}
            value={themeMode}
          >
            <RadioCard id="light-theme" value="light" className="flex-1 cursor-pointer" labelClassName="px-4">
              <ThemeModePreview theme={{ ...webchatTheme, themeMode: 'light' }} />
            </RadioCard>
            <RadioCard id="dark-theme" value="dark" className="flex-1 cursor-pointer" labelClassName="px-4">
              <ThemeModePreview theme={{ ...webchatTheme, themeMode: 'dark' }} />
            </RadioCard>
          </RadioGroup>
        </div>

        <div className="flex w-full flex-col gap-2">
          <Label className="text-neutral-950">Header</Label>
          <RadioGroup
            className="flex w-full flex-col xl:flex-row"
            onValueChange={(value: 'glass' | 'solid') => setHeaderVariant(value)}
            value={headerVariant}
          >
            <RadioCard value="glass" id="glass-header" className="flex-1 cursor-pointer" labelClassName="px-4">
              <HeaderPreview theme={{ ...webchatTheme, headerVariant: 'glass' }} />
            </RadioCard>
            <RadioCard value="solid" id="solid-header" className="flex-1 cursor-pointer" labelClassName="px-4">
              <HeaderPreview theme={{ ...webchatTheme, headerVariant: 'solid' }} />
            </RadioCard>
          </RadioGroup>
        </div>
        <div className="flex w-full flex-col gap-2">
          <Label className="text-neutral-950">Messages</Label>
          <RadioGroup
            className="flex w-full flex-col xl:flex-row"
            onValueChange={(value: 'soft' | 'solid') => setVariant(value)}
            value={variant}
          >
            <RadioCard value="soft" id="soft-message" className="flex-1 cursor-pointer" labelClassName="px-4">
              <MessageListPreview theme={{ ...webchatTheme, variant: 'soft' }} />
            </RadioCard>
            <RadioCard value="solid" id="solid-message" className="flex-1 cursor-pointer" labelClassName="px-4">
              <MessageListPreview theme={{ ...webchatTheme, variant: 'solid' }} />
            </RadioCard>
          </RadioGroup>
        </div>

        <div className="flex w-full flex-col gap-2 pr-8">
          <Label className="text-neutral-950">Radius</Label>
          <div className="flex flex-row gap-4">
            <WebchatComponentPreview
              className="relative h-16 w-16 items-start justify-start rounded-lg border bg-neutral-50 px-0 pl-5 pt-5"
              theme={{ ...webchatTheme, radius }}
            >
              <Container className="!absolute !w-80 overflow-clip !rounded-b-none !rounded-r-none !border-b-0 !border-r-0">
                <Header configuration={{}} />
              </Container>
            </WebchatComponentPreview>
            <Slider
              className="w-full"
              value={[radius ?? 1]}
              onValueChange={(value: number[]) => {
                setRadius(value[0] ?? 1)
              }}
              min={0.5}
              max={4}
              step={0.5}
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-2">
          <Label className="text-neutral-950">Font</Label>
          <FontCombobox fonts={fonts} fontFamily={fontFamily} setFont={setFont} />
        </div>
      </div>
    </ConfigSection>
  )
}

const ThemeModePreview = ({ theme }: { theme: LatestWebchatTheme }) => (
  <WebchatComponentPreview className="h-30 flex-1 bg-transparent pt-4" theme={theme}>
    <Container className="w-full !rounded-b-none !border-b-0">
      <div
        className={cn('flex items-center gap-3 px-4 py-2 transition-all duration-300', {
          'bg-[var(--bpPrimary-600)]': theme.headerVariant === 'solid',
        })}
      >
        <div className="aspect-square h-9 rounded-full bg-[var(--bpGray-200)]"></div>
        <div className="flex w-full flex-col gap-2">
          <div className="h-5 w-10 rounded-full bg-[var(--bpGray-100)]"></div>
          <div className="h-3 w-24 rounded-full bg-[var(--bpGray-200)]"></div>
        </div>
      </div>
      <div className="flex w-full flex-col gap-2 p-4">
        <div className="h-4 w-3/4 self-end rounded-full bg-[var(--bpGray-200)]"></div>
        <div className="h-4 w-3/4 self-start rounded-full bg-[var(--bpGray-200)]"></div>
      </div>
    </Container>
  </WebchatComponentPreview>
)

const HeaderPreview = ({ theme }: { theme: LatestWebchatTheme }) => (
  <WebchatComponentPreview className="pointer-events-none h-20 items-start bg-transparent pt-4" theme={theme}>
    <Container className="w-full !rounded-b-none !border-b-0 ">
      <Header configuration={{}} />
    </Container>
  </WebchatComponentPreview>
)

const MessageListPreview = ({ theme }: { theme: LatestWebchatTheme }) => {
  const messages = [
    {
      direction: 'incoming' as const,
      sender: { name: 'Bot' },
      id: 'incoming-message',
      timestamp: new Date(),
      block: {
        type: 'bubble' as const,
        block: {
          type: 'text' as const,
          text: 'Incoming message',
        },
      },
    },
    {
      direction: 'outgoing' as const,
      sender: { name: 'User' },
      id: 'outgoing-message',
      timestamp: new Date(),
      block: {
        type: 'bubble' as const,
        block: {
          type: 'text' as const,
          text: 'Outgoing message',
        },
      },
    },
  ]

  return (
    <WebchatComponentPreview className="h-32 items-start bg-transparent" theme={theme}>
      <MessageList
        className="bg-neutral-50 !pt-2"
        messages={messages}
        showMarquee={false}
        showDateSystemMessage={false}
        showMessageStatus={false}
        showIncomingMessageAvatar={false}
      />
    </WebchatComponentPreview>
  )
}
